<template>
  <div class="gold-coins-burst" :class="{ 'is-embedded': embedded }">
    <div class="gold-coins-scroll">
      <div class="gold-coins-content">
        <div ref="stageRef" class="gold-coins-stage">
          <div class="light-layer">
            <img class="light-img-shan" :src="lightImg" alt="" />
          </div>

          <div ref="coinLayerBackRef" class="coin-layer coin-layer-back"></div>
          <div ref="coinLayerFrontRef" class="coin-layer coin-layer-front"></div>

          <div class="main-wrap">
            <img class="main-img" :src="mainImg" alt="" />
            <div class="shine-mask" :style="shineMaskStyle" />
            <div class="jackpot-num">
              <span>R$</span>
              <div class="number-container">
                <div ref="refCountup"></div>
                <transition name="jackpot-num-fade">
                  <van-loading v-if="isJackpotLoading" type="spinner" size="16" class="jackpot-num-loading" />
                </transition>
              </div>
            </div>
          </div>
        </div>

        <div class="broadcast-list-container" :style="{ '--broadcast-item-height': `${BROADCAST_ITEM_HEIGHT}px` }">
          <div v-if="broadcastList.length" class="broadcast-viewport">
            <div class="broadcast-track" :style="broadcastTrackStyle" @transitionend="handleBroadcastTransitionEnd">
              <div v-for="(item, index) in broadcastDisplayList" :key="`broadcast-${index}`" class="broadcast-item">
                <div class="broadcast-message">
                  <div class="broadcast-message-text">
                    <span class="broadcast-message-line">
                      {{ getBroadcastLines(item).firstLine }}
                      <span v-if="!getBroadcastLines(item).secondLine && item.amount != null" class="broadcast-amount">
                        R${{ item.amount }}
                      </span>
                    </span>
                    <span v-if="getBroadcastLines(item).secondLine" class="broadcast-message-line">
                      {{ getBroadcastLines(item).secondLine }}
                      <span v-if="item.amount != null" class="broadcast-amount">R${{ item.amount }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button class="button-wrap" @click="handleDeposito">
          <span class="button-text">Depósito agora</span>
          <img :src="buttonImg" alt="" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { CountUp } from 'countup.js'
import { jackpotApi } from '@/api/home'
import buttonImg from '@/assets/theme/gold_coins/button.png'
import mainImg from '@/assets/theme/gold_coins/main_img.webp'
import lightImg from '@/assets/theme/gold_coins/light.png'
import goldImg from '@/assets/theme/gold_coins/gold.png'
import contourMask from '@/assets/theme/gold_coins/contour.svg'
import defaultImg from '@/assets/theme/empty/placeholder.png'

const props = defineProps({
  /** 挂载后自动播放金币爆发，默认 true */
  autoplay: {
    type: Boolean,
    default: true,
  },
  /** 是否循环持续爆金币，默认 true；false 时首轮爆发后触发 complete */
  loop: {
    type: Boolean,
    default: true,
  },
  /** true 时停止动画，默认 false */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** 弹窗嵌入模式：由父级接管滚动，组件只占内容高度 */
  embedded: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['complete', 'deposito'])

const stageRef = ref(null)
const coinLayerBackRef = ref(null)
const coinLayerFrontRef = ref(null)

const STAGE_W = 375
const ORIGIN_X = STAGE_W / 2
const ORIGIN_Y = 238

/**
 * 金币动画配置
 *
 * poolSize：对象池容量。复用 DOM 节点避免高频创建/销毁，降低 GC 压力；
 *           已比旧版减少约 1/3（47 枚），不要再增加，否则画面过密、遮挡主图。
 * burstMin / burstMax：每轮爆发生成的金币数量范围，控制持续爆发密度。
 * firstBurstCount：首屏爆发数量，控制开场视觉冲击感。
 * interval：持续爆发间隔（ms），控制金币整体密度与节奏。
 */
const COIN_CONFIG = {
  poolSize: 47,
  burstMin: 3,
  burstMax: 6,
  firstBurstCount: 21,
  interval: 520,
}

const shineMaskStyle = computed(() => ({
  WebkitMaskImage: `url("${contourMask}")`,
  maskImage: `url("${contourMask}")`,
}))

const rand = (min, max) => Math.random() * (max - min) + min
const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

let pool = []
let rafId = null
let lastTime = 0
let burstTimer = 0
/** 是否继续调度新一轮爆发（stop 后为 false，已有金币仍自然播完） */
let isScheduling = false
/** loop=false 时，首轮爆发后不再调度，待全部金币结束后触发 complete */
let awaitingComplete = false
let hasEmittedComplete = false
let poolInitialized = false

const refCountup = ref(null)
const countUpInstance = ref(null)
const jackpotUpdateTimer = ref(null)
const isJackpotAnimating = ref(false)
const jackpotData = ref({ broadcast_list: [], jackpot_amount: 1000000 })
const isJackpotLoading = ref(true)

const broadcastList = computed(() => jackpotData.value.broadcast_list || [])
const broadcastDisplayList = computed(() => {
  const list = broadcastList.value || []
  return list.length > 1 ? [...list, list[0]] : list
})

const BROADCAST_ITEM_HEIGHT = 56
const BROADCAST_TRANSITION_MS = 500

const broadcastActiveIndex = ref(0)
const isBroadcastTransitionEnabled = ref(true)
let broadcastTimer = null
let broadcastSnapTimer = null

const broadcastTrackStyle = computed(() => ({
  transform: `translate3d(0, -${broadcastActiveIndex.value * BROADCAST_ITEM_HEIGHT}px, 0)`,
  transition: isBroadcastTransitionEnabled.value ? `transform ${BROADCAST_TRANSITION_MS}ms ease` : 'none',
}))

const startBroadcastLoop = () => {
  stopBroadcastLoop()

  if (!broadcastList.value || broadcastList.value.length <= 1) {
    broadcastActiveIndex.value = 0
    isBroadcastTransitionEnabled.value = true
    return
  }

  broadcastTimer = window.setInterval(() => {
    clearBroadcastSnapTimer()
    isBroadcastTransitionEnabled.value = true
    broadcastActiveIndex.value += 1

    if (broadcastActiveIndex.value >= broadcastList.value.length) {
      broadcastSnapTimer = window.setTimeout(snapBroadcastToStart, BROADCAST_TRANSITION_MS + 50)
    }
  }, 3000)
}

const stopBroadcastLoop = () => {
  if (broadcastTimer) {
    window.clearInterval(broadcastTimer)
    broadcastTimer = null
  }
  clearBroadcastSnapTimer()
}

const clearBroadcastSnapTimer = () => {
  if (broadcastSnapTimer) {
    window.clearTimeout(broadcastSnapTimer)
    broadcastSnapTimer = null
  }
}

const snapBroadcastToStart = () => {
  if (broadcastList.value.length <= 1 || broadcastActiveIndex.value < broadcastList.value.length) {
    return
  }

  clearBroadcastSnapTimer()
  isBroadcastTransitionEnabled.value = false
  broadcastActiveIndex.value = 0

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      isBroadcastTransitionEnabled.value = true
    })
  })
}

const handleBroadcastTransitionEnd = () => {
  snapBroadcastToStart()
}

const buildBroadcastText = (item) => {
  const parts = []
  if (item?.username) parts.push(item.username)
  if (item?.formatted_message) parts.push(item.formatted_message)
  return parts.join(' ')
}

const splitBroadcastTextByEm = (text) => {
  if (!text) {
    return {
      firstLine: '',
      secondLine: '',
    }
  }

  const match = text.match(/\s+em\s+/i)

  if (!match || match.index === undefined) {
    return {
      firstLine: text,
      secondLine: '',
    }
  }

  const splitIndex = match.index

  return {
    firstLine: text.slice(0, splitIndex).trim(),
    secondLine: text.slice(splitIndex).trim(),
  }
}

const getBroadcastLines = (item) => splitBroadcastTextByEm(buildBroadcastText(item))

const ensureBroadcastList = () => {
  if (!jackpotData.value.broadcast_list?.length) {
    jackpotData.value.broadcast_list = generateDefaultData()
  }
}

const generateDefaultData = () => {
  return Array.from({ length: 10 }, (_, index) => ({
    amount: (Math.random() * 1000 + 100).toFixed(2),
    game_name: `Game ${index + 1}`,
    username: `User${index + 1}`,
    formatted_message: '',
    game_image: defaultImg,
  }))
}

const getRandomJackpotFloat = () => Number((Math.random() * 4 + 1).toFixed(2))

const updateJackpotCount = () => {
  if (!countUpInstance.value || isJackpotAnimating.value) return

  isJackpotAnimating.value = true
  jackpotData.value.jackpot_amount += getRandomJackpotFloat()

  if (jackpotData.value.jackpot_amount > 9999999999) {
    jackpotData.value.jackpot_amount = 1000000
  }

  countUpInstance.value.update(jackpotData.value.jackpot_amount)

  setTimeout(() => {
    isJackpotAnimating.value = false
  }, 1000)
}

const initJackpotCountUp = () => {
  if (!refCountup.value) return

  try {
    countUpInstance.value = new CountUp(refCountup.value, jackpotData.value.jackpot_amount, {
      duration: 1,
      separator: ',',
      decimal: '.',
      decimalPlaces: 2,
      startVal: jackpotData.value.jackpot_amount,
      useGrouping: true,
      useEasing: true,
      onComplete: () => {
        isJackpotAnimating.value = false
      },
    })

    if (!countUpInstance.value.error) {
      countUpInstance.value.start()
      jackpotUpdateTimer.value = setInterval(updateJackpotCount, 1000)
    }
  } catch (error) {
    console.error('初始化博彩金额动画失败:', error)
  }
}

const fetchJackpotAmount = async () => {
  try {
    const { data, code } = await jackpotApi()
    if (code == 1 && data) {
      jackpotData.value = {
        broadcast_list: data.broadcast_list || [],
        jackpot_amount: data.jackpot_amount ?? jackpotData.value.jackpot_amount,
      }
    }
    ensureBroadcastList()
  } catch (error) {
    console.error('获取博彩金额失败:', error)
    ensureBroadcastList()
  } finally {
    isJackpotLoading.value = false
    nextTick(() => {
      initJackpotCountUp()
    })
  }
}

const cleanupJackpot = () => {
  if (jackpotUpdateTimer.value) {
    clearInterval(jackpotUpdateTimer.value)
    jackpotUpdateTimer.value = null
  }
  isJackpotAnimating.value = false
}

const createCoin = (layer) => {
  const el = document.createElement('div')
  el.className = 'coin'

  const img = document.createElement('img')
  img.src = goldImg
  img.alt = ''
  el.appendChild(img)

  layer.appendChild(el)
  return {
    el,
    active: false,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    gravity: 0,
    age: 0,
    duration: 1200,
    rotate: 0,
    rotateSpeed: 0,
    baseScale: 1,
    drift: 0,
  }
}

const initPool = () => {
  if (poolInitialized) return true

  const layerBack = coinLayerBackRef.value
  const layerFront = coinLayerFrontRef.value

  if (!layerBack || !layerFront) {
    return false
  }

  pool = Array.from({ length: COIN_CONFIG.poolSize }, (_, i) => {
    // 约 80% 金币在主图后层，少量走前层制造层次，但不强遮挡主图
    const useFrontLayer = i > COIN_CONFIG.poolSize * 0.8
    return createCoin(useFrontLayer ? layerFront : layerBack)
  })

  poolInitialized = true
  return true
}

const getIdleCoin = () => pool.find((item) => !item.active)

const hasActiveCoins = () => pool.some((c) => c.active)

const spawnCoin = () => {
  const c = getIdleCoin()
  if (!c) return

  const sideBias = Math.random() < 0.5 ? -1 : 1
  const angle = (rand(-158, -22) * Math.PI) / 180
  const speed = rand(250, 470)

  c.active = true
  c.age = 0
  c.duration = rand(1050, 1700)
  c.x = ORIGIN_X + rand(-34, 34)
  c.y = ORIGIN_Y + rand(-18, 28)

  // 水平速度侧向增强，确保从主图背后明显向外爆开
  c.vx = Math.cos(angle) * speed + sideBias * rand(25, 90)
  c.vy = Math.sin(angle) * speed - rand(10, 80)
  c.gravity = rand(420, 660)

  c.rotate = rand(0, 360)
  c.rotateSpeed = rand(-820, 820)
  c.baseScale = rand(0.48, 1.1)
  c.drift = rand(-22, 22)

  c.el.style.opacity = '1'
  c.el.style.transform = `translate3d(${c.x - 23}px, ${c.y - 21}px, 0) rotate(${c.rotate}deg) scale(${c.baseScale})`
}

const burst = (count) => {
  for (let i = 0; i < count; i++) spawnCoin()
}

const hideCoin = (c) => {
  c.active = false
  c.el.style.opacity = '0'
  c.el.style.transform = 'translate3d(-9999px, -9999px, 0)'
}

const updateCoin = (c, dt) => {
  c.age += dt
  const t = c.age / c.duration

  if (t >= 1) {
    hideCoin(c)
    return
  }

  const sec = c.age / 1000

  // 抛物线：x 线性外扩 + 小幅横向漂移；y 先上抛，后受重力下坠
  const x = c.x + c.vx * sec + Math.sin(t * Math.PI) * c.drift
  const y = c.y + c.vy * sec + 0.5 * c.gravity * sec * sec

  const scale = c.baseScale * (0.72 + Math.sin(Math.PI * t) * 0.48) * (1 - t * 0.22)
  const opacity = t < 0.66 ? 1 : 1 - (t - 0.66) / 0.34
  const rotate = c.rotate + c.rotateSpeed * sec

  c.el.style.opacity = String(clamp(opacity, 0, 1))
  c.el.style.transform = `translate3d(${x - 23}px, ${y - 21}px, 0) rotate(${rotate}deg) scale(${scale})`
}

const maybeEmitComplete = () => {
  if (awaitingComplete && !hasEmittedComplete && !hasActiveCoins() && !isScheduling) {
    hasEmittedComplete = true
    emit('complete')
  }
}

const tick = (time) => {
  const dt = Math.min(time - lastTime || 16.7, 40)
  lastTime = time

  if (isScheduling) {
    burstTimer += dt
    if (burstTimer >= COIN_CONFIG.interval) {
      burstTimer = 0
      burst(Math.floor(rand(COIN_CONFIG.burstMin, COIN_CONFIG.burstMax + 1)))
    }
  }

  for (const c of pool) {
    if (c.active) updateCoin(c, dt)
  }

  maybeEmitComplete()

  if (isScheduling || hasActiveCoins()) {
    rafId = requestAnimationFrame(tick)
  } else {
    rafId = null
  }
}

const startRafLoop = () => {
  if (rafId !== null) return
  lastTime = 0
  rafId = requestAnimationFrame(tick)
}

const beginBurstPlayback = () => {
  // loop=false 时只播首轮爆发，不再调度后续 interval 爆发
  isScheduling = props.loop
  burstTimer = 0
  awaitingComplete = !props.loop
  hasEmittedComplete = false

  burst(COIN_CONFIG.firstBurstCount)
  startRafLoop()
}

const play = () => {
  if (props.disabled) return

  const ready = initPool()
  if (!ready) {
    // 弹窗 lazy-render 等场景下 refs 可能尚未就绪，下一帧重试初始化
    nextTick(() => {
      if (props.disabled) return
      if (!initPool()) return
      beginBurstPlayback()
    })
    return
  }

  beginBurstPlayback()
}

/**
 * 停止继续生成金币，但允许场上已有金币自然播完至淡出，
 * 避免突然清空造成视觉跳变；reset() 可立即清空。
 */
const stop = () => {
  isScheduling = false
  if (!hasActiveCoins()) {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    maybeEmitComplete()
  }
}

const reset = () => {
  isScheduling = false
  awaitingComplete = false
  hasEmittedComplete = false
  burstTimer = 0
  lastTime = 0

  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }

  for (const c of pool) {
    hideCoin(c)
  }
}

const cleanup = () => {
  stop()
  reset()

  for (const c of pool) {
    c.el.remove()
  }
  pool = []
  poolInitialized = false
}

const handleDeposito = () => {
  emit('deposito')
}

defineExpose({
  play,
  stop,
  reset,
})

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) {
      stop()
    } else if (props.autoplay) {
      play()
    }
  }
)

watch(
  () => broadcastList.value.length,
  () => {
    broadcastActiveIndex.value = 0
    startBroadcastLoop()
  }
)

onMounted(async () => {
  await nextTick()

  initPool()
  fetchJackpotAmount()
  startBroadcastLoop()

  if (props.autoplay && !props.disabled) {
    play()
  }
})

onBeforeUnmount(() => {
  stopBroadcastLoop()
  cleanupJackpot()
  cleanup()
})
</script>

<style lang="less" scoped>
.gold-coins-burst {
  width: 100%;
  height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  pointer-events: none;
}

.gold-coins-scroll {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: max(12px, env(safe-area-inset-top)) 0 max(24px, env(safe-area-inset-bottom));
}

.gold-coins-content {
  width: min(375px, 100vw);
  min-height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
}

.gold-coins-stage {
  position: relative;
  width: 375px;
  height: 335px;
  max-width: 100vw;
  flex: 0 0 auto;
  overflow: visible;
  transform: translateZ(0);
  pointer-events: none;

  .light-layer {
    position: absolute;
    width: 100%;
    top: 28%;
    left: 50%;
    transform: translate(-50%, -56%);
    transform-origin: center center;
    opacity: 0.78;
    mix-blend-mode: screen;
    pointer-events: none;
    z-index: 1;
    animation: light-img-shan-rotate 6s linear infinite;

    .light-img-shan {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }
  }

  .coin-layer {
    position: absolute;
    inset: 0;
    overflow: visible;
    pointer-events: none;
    transform: translateZ(0);

    &-back {
      z-index: 2;
    }

    &-front {
      z-index: 4;
    }
  }

  .main-wrap {
    position: absolute;
    width: 340px;
    height: 365px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.8);
    transform-origin: top center;
    z-index: 3;

    .main-img {
      position: relative;
      z-index: 1;
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .shine-mask {
      position: absolute;
      inset: 0;
      z-index: 2;
      overflow: hidden;
      pointer-events: none;
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      -webkit-mask-position: center;
      mask-position: center;
      -webkit-mask-size: 100% 100%;
      mask-size: 100% 100%;

      &::after {
        content: '';
        position: absolute;
        top: -40%;
        left: -70%;
        width: 34%;
        height: 190%;
        transform: rotate(21deg);
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.08) 20%,
          rgba(255, 255, 255, 0.82) 48%,
          rgba(255, 238, 128, 0.62) 56%,
          transparent 100%
        );
        filter: blur(0.5px);
        mix-blend-mode: screen;
        animation: shineSweep 2.45s cubic-bezier(0.22, 0.8, 0.2, 1) infinite;
      }
    }

    .jackpot-num {
      position: absolute;
      left: 50%;
      bottom: 9%;
      transform: translateX(-50%);
      z-index: 3;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 54%;
      min-height: 28px;
      pointer-events: none;
      font-weight: 700;
      font-size: 22px;
      line-height: 1.2;
      color: #fff800;

      .number-container {
        position: relative;
        display: flex;
        align-items: center;
        min-width: 48px;
        min-height: 18px;
        margin-left: 2px;
        letter-spacing: -0.5px;
        color: #fff800;
      }

      .jackpot-num-loading {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        color: #fff800;
      }
    }

    .jackpot-num-fade-leave-active {
      transition: opacity 0.35s ease;
    }

    .jackpot-num-fade-leave-to {
      opacity: 0;
    }
  }

  :deep(.coin) {
    position: absolute;
    left: 0;
    top: 0;
    width: 46px;
    height: 42px;
    opacity: 0;
    pointer-events: none;
    transform-origin: center center;
    will-change: transform, opacity;
    filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.35));

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }
  }
}

.broadcast-list-container {
  --broadcast-line-height: 20px;

  position: relative;
  width: 100%;
  height: var(--broadcast-item-height);
  min-height: var(--broadcast-item-height);
  max-height: var(--broadcast-item-height);
  flex: 0 0 var(--broadcast-item-height);
  margin-top: -60px;
  z-index: 5;
  overflow: hidden;
}

.broadcast-viewport {
  position: relative;
  width: 100%;
  height: var(--broadcast-item-height);
  overflow: hidden;
}

.broadcast-track {
  width: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 500ms ease;
  will-change: transform;
}

.broadcast-item {
  width: 100%;
  height: var(--broadcast-item-height);
  min-height: var(--broadcast-item-height);
  max-height: var(--broadcast-item-height);
  flex: 0 0 var(--broadcast-item-height);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  box-sizing: border-box;
}

.broadcast-message {
  width: 100%;
  height: var(--broadcast-item-height);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  box-sizing: border-box;
}

.broadcast-message-text {
  max-width: 80%;
  height: calc(var(--broadcast-line-height) * 2);
  max-height: calc(var(--broadcast-line-height) * 2);
  // background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: var(--broadcast-line-height);
  text-align: center;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  box-sizing: border-box;
}

.broadcast-message-line {
  display: block;
  width: 100%;
  height: var(--broadcast-line-height);
  line-height: var(--broadcast-line-height);
  text-align: center;
  white-space: normal;
  overflow: hidden;
}

.broadcast-amount {
  color: #9aff00;
  margin-left: 4px;
  white-space: nowrap;
}

.button-wrap {
  position: relative;
  width: 230px;
  height: auto;
  margin-top: 32px;
  z-index: 6;
  padding: 0;
  border: none;
  background: transparent;
  pointer-events: auto;
  cursor: pointer;

  img {
    width: 100%;
    display: block;
  }

  .button-text {
    position: absolute;
    left: 50%;
    top: 46%;
    transform: translate(-50%, -50%);
    font-size: 16px;
    font-weight: 700;
    color: #202125;
    white-space: nowrap;
  }
}

@keyframes light-img-shan-rotate {
  0% {
    transform: translate(-50%, -56%) rotate(0deg);
  }

  100% {
    transform: translate(-50%, -56%) rotate(360deg);
  }
}

@keyframes shineSweep {
  0% {
    transform: translateX(-15%) rotate(21deg);
    opacity: 0;
  }

  14% {
    opacity: 0.95;
  }

  48% {
    opacity: 0.95;
  }

  70%,
  100% {
    transform: translateX(560%) rotate(21deg);
    opacity: 0;
  }
}

/* 弹窗嵌入模式：让外层 Popup 接管滚动，组件本身只占内容高度 */
.gold-coins-burst.is-embedded {
  height: auto;
  max-height: none;
  overflow: visible;
}

.gold-coins-burst.is-embedded .gold-coins-scroll {
  height: auto;
  overflow: visible;
  padding: 0;
}

.gold-coins-burst.is-embedded .gold-coins-content {
  min-height: 0;
}
</style>
